import { ILocalStore } from '@kts-front/types';
import dayjs from 'dayjs';
import { computed, IReactionDisposer, makeObservable, reaction } from 'mobx';

import { OptionEntity } from 'types/OptionEntity';
import { getMinDate } from 'utils/getMinDate';
import { validateNotEmpty } from 'utils/validators';

import { FieldSelectProjectStore } from '../FieldSelectProjectStore';
import { FieldSelectTimeSlotStore } from '../FieldSelectTimeSlotStore';
import { FieldDateStore } from '../form/FieldDateStore';
import { FieldSelectMultiStore } from '../form/FieldSelectStore';

type Params = {
  isProjectSelectRequired: boolean;
  isProjectSelectShown: boolean;
  isSlotSelectRequired: boolean;
  isSlotSelectShown: boolean;
  getLeadId?: () => number | null;
};

export class ProjectTimeSlotStore implements ILocalStore {
  projects: FieldSelectProjectStore;

  readonly date = new FieldDateStore({ defaultValue: getMinDate(new Date()), required: true });

  readonly isOffline: FieldSelectMultiStore;

  slot: FieldSelectTimeSlotStore;

  private readonly _disposers: IReactionDisposer[];

  constructor({
    isProjectSelectRequired,
    isProjectSelectShown,
    isSlotSelectRequired,
    isSlotSelectShown,
    getLeadId,
  }: Params) {
    this.projects = new FieldSelectProjectStore({
      required: isProjectSelectRequired,
      shown: isProjectSelectShown,
      validator: isProjectSelectRequired ? validateNotEmpty('Выберите проект') : undefined,
    });

    this.slot = new FieldSelectTimeSlotStore({
      required: isSlotSelectRequired,
      shown: isSlotSelectShown,
      validator: isSlotSelectRequired ? validateNotEmpty('Выберите время') : undefined,
      getLeadId,
    });

    this.isOffline = (() => {
      const store = new FieldSelectMultiStore<OptionEntity>({
        defaultValue: [{ key: 'false', value: 'Онлайн' }],
        required: true,
        getOptions: async () => ({
          isError: false,
          data: {
            items: [
              { key: 'true', value: 'Офлайн' },
              { key: 'false', value: 'Онлайн' },
            ],
            total: 2,
          },
        }),
      });

      store.load();

      return store;
    })();

    this._disposers = [
      reaction(
        () => [this.date.value, this.projects.value[0]?.key, this.isOffline.value],
        () => {
          const projectKey = this.projects.value[0]?.key;

          if (!projectKey || !this.date.value) {
            this.slot.reset();

            return;
          }

          const isOfflineValue = this.isOffline.value[0]?.key === 'true';

          this.slot.updateSlot(dayjs(this.date.value).format('YYYY-MM-DD'), projectKey, isOfflineValue);
        },
      ),
    ];

    makeObservable(this, {
      shouldShowSlot: computed,
    });
  }

  get shouldShowSlot(): boolean {
    const isDateInPast = this.date.value ? dayjs(this.date.value).isBefore(dayjs().startOf('day')) : false;

    return Boolean(this.slot.projectId.value) && !isDateInPast;
  }

  get isError(): boolean {
    return (
      (this.projects.shown && this.projects.hasError) ||
      (this.slot.shown && this.slot.hasError && this.slot.touched) ||
      (this.slot.shown && !this.date.value)
    );
  }

  get isTouched(): boolean {
    if (this.projects.required && this.projects.shown) {
      return this.projects.touched && Boolean(this.slot.touched);
    }

    return true;
  }

  validate(): boolean {
    this.projects.validate();
    this.slot.validate();

    return this.isError;
  }

  resetFields() {
    this.projects.reset();
    this.projects.projectId.change(null);
    this.slot.reset();
    this.isOffline.reset();
  }

  destroy(): void {
    this._disposers.forEach((d) => d());
  }
}
